import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Check, Sparkles, Zap, TrendingUp } from 'lucide-react';
import { stripeService } from '../services/stripe';
import { toast } from 'sonner';

export default function OnboardingModal({ user, onComplete, onSkip }) {
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState('free');
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [loading, setLoading] = useState(false);

  // Prices must match subscription_access.PLAN_PRICES ($0/$79/$299/$999)
  const plans = [
    {
      id: 'free',
      name: 'Free',
      price: 0,
      features: [
        'Up to 10 products',
        '5 deals/mo on thedeals.ai',
        'Money Found weekly statement',
        '30 WhatsApp/SMS msgs/mo included'
      ],
      cta: 'Start with Free',
      popular: false
    },
    {
      id: 'starter',
      name: 'Starter',
      monthlyPrice: 79,
      annualPrice: 63,
      features: [
        'Unlimited products & deals',
        'Unlimited thedeals.ai publishing',
        '10 AI recommendations/month',
        '500 msgs/mo included'
      ],
      cta: 'Start 14-Day Trial',
      popular: true
    },
    {
      id: 'pro',
      name: 'Pro',
      monthlyPrice: 299,
      annualPrice: 239,
      features: [
        'Everything in Starter',
        'Unlimited AI recommendations',
        'Slack approvals & decision traces',
        '2,000 msgs/mo included'
      ],
      cta: 'Start 14-Day Trial',
      popular: false
    },
    {
      id: 'scale',
      name: 'Scale',
      monthlyPrice: 999,
      annualPrice: 799,
      features: [
        'Everything in Pro',
        'Signals API & team workflows',
        'Funding pack & USDC payouts',
        '10,000 msgs/mo included'
      ],
      cta: 'Start 14-Day Trial',
      popular: false
    }
  ];

  const getPrice = (plan) => {
    if (plan.id === 'free') return 0;
    return billingCycle === 'monthly' ? plan.monthlyPrice : plan.annualPrice;
  };

  const handleSelectPlan = async () => {
    if (selectedPlan === 'free') {
      // No payment needed for free plan
      onComplete('free');
      return;
    }

    setLoading(true);
    try {
      // Create Stripe checkout session (args: plan, cycle, addons, email, merchantId)
      const session = await stripeService.createCheckoutSession(
        selectedPlan,
        billingCycle,
        null,
        user?.email || null,
        user?.merchant_id || null
      );

      // Redirect to Stripe
      window.location.href = session.url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error('Failed to start checkout', {
        description: 'Please try again or contact support'
      });
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div className="bg-[#0E1426] rounded-2xl border border-[#22304A] w-full max-w-6xl max-h-[90vh] overflow-y-auto md:rounded-2xl rounded-lg">
        {/* Header */}
        <div className="sticky top-0 bg-[#0E1426] border-b border-[#22304A] p-4 md:p-6 flex items-center justify-between">
          <div>
            <h2 className="text-xl md:text-2xl font-bold text-white mb-2">Choose Your Plan</h2>
            <p className="text-sm md:text-base text-[#9CB0C7]">Select the plan that fits your business needs</p>
          </div>
          <button
            onClick={onSkip}
            className="text-[#9CB0C7] hover:text-white transition-colors"
          >
            <X className="w-5 h-5 md:w-6 md:h-6" />
          </button>
        </div>

        {/* Billing Toggle */}
        <div className="p-4 md:p-6 border-b border-[#22304A] flex justify-center">
          <div className="inline-flex items-center gap-2 md:gap-4 bg-[#111A2E] rounded-lg p-1 w-full md:w-auto">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`flex-1 md:flex-none px-4 md:px-6 py-2 rounded-md text-sm md:text-base font-medium transition-colors ${
                billingCycle === 'monthly'
                  ? 'bg-[#7A5CFF] text-white'
                  : 'text-[#9CB0C7] hover:text-white'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`flex-1 md:flex-none px-4 md:px-6 py-2 rounded-md text-sm md:text-base font-medium transition-colors ${
                billingCycle === 'annual'
                  ? 'bg-[#7A5CFF] text-white'
                  : 'text-[#9CB0C7] hover:text-white'
              }`}
            >
              Annual
              <span className="ml-2 text-xs bg-[#8CF78E]/20 text-[#8CF78E] px-2 py-0.5 rounded-full">
                Save 20%
              </span>
            </button>
          </div>
        </div>

        {/* Plans */}
        <div className="p-4 md:p-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
          {plans.map((plan) => (
            <div
              key={plan.id}
              onClick={() => setSelectedPlan(plan.id)}
              className={`cursor-pointer rounded-xl border-2 p-6 transition-all ${
                selectedPlan === plan.id
                  ? 'border-[#3EF1C7] bg-[#3EF1C7]/5 shadow-lg shadow-[#3EF1C7]/20'
                  : 'border-[#22304A] bg-[#111A2E] hover:border-[#3EF1C7]/50'
              } ${plan.popular ? 'ring-2 ring-[#7A5CFF] ring-offset-2 ring-offset-[#0E1426]' : ''}`}
            >
              {plan.popular && (
                <div className="mb-3">
                  <span className="px-3 py-1 text-xs rounded-full bg-[#7A5CFF] text-white font-medium">
                    Most Popular
                  </span>
                </div>
              )}

              <h3 className="text-xl font-bold text-white mb-2">{plan.name}</h3>
              
              <div className="mb-6">
                {plan.id === 'free' ? (
                  <div className="text-3xl font-bold text-white">$0</div>
                ) : (
                  <>
                    <div className="text-3xl font-bold text-white">
                      ${getPrice(plan)}
                      <span className="text-base text-[#9CB0C7] font-normal">
                        /{billingCycle === 'monthly' ? 'mo' : 'mo'}
                      </span>
                    </div>
                    {billingCycle === 'annual' && (
                      <p className="text-xs text-[#8CF78E] mt-1">
                        Billed ${getPrice(plan) * 12}/year
                      </p>
                    )}
                    {billingCycle === 'monthly' && (
                      <p className="text-xs text-[#3EF1C7] mt-1">
                        🎁 14-day free trial
                      </p>
                    )}
                  </>
                )}
              </div>

              <ul className="space-y-3 mb-6">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start gap-2 text-sm text-[#9CB0C7]">
                    <Check className="w-4 h-4 text-[#3EF1C7] flex-shrink-0 mt-0.5" />
                    <span>{feature}</span>
                  </li>
                ))}
              </ul>

              <div className={`w-full py-2 px-4 rounded-lg text-center font-medium transition-colors ${
                selectedPlan === plan.id
                  ? 'bg-[#3EF1C7] text-[#0B1120]'
                  : 'bg-[#1A2438] text-[#9CB0C7]'
              }`}>
                {selectedPlan === plan.id ? '✓ Selected' : 'Select Plan'}
              </div>
            </div>
          ))}
        </div>

        {/* Footer CTA */}
        <div className="sticky bottom-0 bg-[#0E1426] border-t border-[#22304A] p-6">
          <div className="flex items-center justify-between max-w-2xl mx-auto">
            <button
              onClick={onSkip}
              className="px-6 py-3 text-[#9CB0C7] hover:text-white transition-colors"
            >
              Skip for now
            </button>
            <button
              onClick={handleSelectPlan}
              disabled={loading}
              className="px-8 py-3 bg-gradient-to-r from-[#3EF1C7] to-[#7A5CFF] text-white rounded-lg hover:opacity-90 transition-opacity font-semibold disabled:opacity-50 flex items-center gap-2"
            >
              {loading ? (
                'Processing...'
              ) : selectedPlan === 'free' ? (
                <>
                  <Zap className="w-5 h-5" />
                  Get Started Free
                </>
              ) : billingCycle === 'monthly' ? (
                <>
                  <Sparkles className="w-5 h-5" />
                  Start 14-Day Free Trial
                </>
              ) : (
                <>
                  <TrendingUp className="w-5 h-5" />
                  Subscribe Now (${getPrice(plans.find(p => p.id === selectedPlan)) * 12}/year)
                </>
              )}
            </button>
          </div>
          
          {billingCycle === 'annual' && selectedPlan !== 'free' && (
            <p className="text-center text-xs text-[#9CB0C7] mt-3">
              💳 You'll be charged ${getPrice(plans.find(p => p.id === selectedPlan)) * 12} today for the full year
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
